import { useMemo, useRef } from 'react';
import { PiArrowDown, PiLifebuoyLight, PiWarningCircle } from 'react-icons/pi';
import type { EmployeeAssistance } from 'src/api/generated';
import { Alert } from 'src/modules/shared/components/atoms/alert';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { CardSkeleton } from 'src/modules/shared/components/atoms/cards/card-skeleton';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import { DateGroupItems } from 'src/modules/shared/components/molecules/date-group/date-group-items';
import { EmployeeAssistanceCard } from 'src/modules/automations/components/molecules/employees/assistances/assistance-card';
import { useInfiniteScroll } from 'src/modules/shared/hooks/scroll/movement';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { EmployeeDisplayIdentity } from 'src/modules/automations/types/employees/employee-display-identity.type';
import { toCalendarDateToken } from 'src/modules/shared/utils/date-range.util';

interface EmployeeAssistancesPanelProps {
  /** Loaded assistance records. */
  assistances: EmployeeAssistance[];
  /** Display identity lookup keyed by AI Agent ID. */
  agentIdentities: ReadonlyMap<string, EmployeeDisplayIdentity>;
  /** Opens one source execution. */
  onOpenExecution?: (assistance: EmployeeAssistance) => void;
  /** Opens one CRM conversation. */
  onOpenConversation: (assistance: EmployeeAssistance) => void;
  /** Dismisses one open assistance. */
  onDismissAssistance?: (assistance: EmployeeAssistance) => void;
  /** Assistance currently being dismissed. */
  dismissingAssistanceId?: string;
  /** Loads another cursor page. */
  onLoadMore: () => void;
  /** Whether another cursor page exists. */
  hasMore: boolean;
  /** Whether another cursor page is loading. */
  isLoadingMore: boolean;
  /** Whether the first page is loading. */
  isInitialLoading: boolean;
  /** Whether employee identities stopped at a safety boundary. */
  directoryIncomplete: boolean;
  /** Whether assistance pagination stopped at a repeated cursor. */
  assistanceListIncomplete: boolean;
}

interface AssistanceDateGroup {
  /** Local calendar date shared by the group. */
  date: Date;
  /** Assistance records in API order. */
  assistances: EmployeeAssistance[];
}

const groupAssistancesByDate = (
  assistances: EmployeeAssistance[]
): AssistanceDateGroup[] => {
  const groups = new Map<string, AssistanceDateGroup>();

  assistances.forEach((assistance) => {
    const date = new Date(assistance.updatedAt);
    const key = toCalendarDateToken(date);
    const group = groups.get(key);

    if (group) {
      group.assistances.push(assistance);
    } else {
      groups.set(key, { date, assistances: [assistance] });
    }
  });

  return Array.from(groups.values());
};

/**
 * Renders the paginated assistance list grouped by local calendar date.
 *
 * @param props - Assistance records, loading state, and navigation callbacks.
 * @returns The assistance list, loading skeletons, or empty state.
 */
export const EmployeeAssistancesPanel: React.FC<
  EmployeeAssistancesPanelProps
> = ({
  assistances,
  agentIdentities,
  onOpenExecution,
  onOpenConversation,
  onDismissAssistance,
  dismissingAssistanceId,
  onLoadMore,
  hasMore,
  isLoadingMore,
  isInitialLoading,
  directoryIncomplete,
  assistanceListIncomplete,
}) => {
  const { t } = useI18n();
  const containerRef = useRef<HTMLDivElement>(null);
  const groups = useMemo(
    () => groupAssistancesByDate(assistances),
    [assistances]
  );

  useInfiniteScroll({
    containerRef,
    isFetching: isLoadingMore,
    hasMore,
    fetchMore: onLoadMore,
    direction: 'down',
    threshold: 0.3,
  });

  return (
    <div
      ref={containerRef}
      className="min-h-0 flex-1 overflow-auto bg-slate-50 p-4 sm:p-5"
      aria-label={t('automations.aiAgents.assistance.title')}
      data-testid="assistances-scroll"
    >
      <div className="mx-auto w-full max-w-6xl">
        {directoryIncomplete ? (
          <Alert
            variant="warning"
            appearance="soft"
            icon={<PiWarningCircle className="h-4 w-4" />}
            message={t(
              'automations.aiAgents.assistance.partialDirectoryWarning'
            )}
            className="mb-4"
          />
        ) : null}
        {assistanceListIncomplete ? (
          <Alert
            variant="warning"
            appearance="soft"
            icon={<PiWarningCircle className="h-4 w-4" />}
            message={t('automations.aiAgents.assistance.partialListWarning')}
            className="mb-4"
          />
        ) : null}
        {isInitialLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }, (_, index) => (
              <CardSkeleton
                key={index}
                className="min-h-52 border border-neutral-100"
                header={{ title: true, subtitle: true, action: true }}
                content={{ lines: 3, widths: ['w-1/3', 'w-full', 'w-2/3'] }}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-5">
            {assistances.length === 0 ? (
              <div className="flex min-h-96 items-center justify-center">
                <EmptyState
                  title={t('automations.aiAgents.assistance.emptyTitle')}
                  description={t(
                    'automations.aiAgents.assistance.emptyDescription'
                  )}
                  icon={<PiLifebuoyLight className="h-6 w-6" />}
                />
              </div>
            ) : (
              groups.map((group) => (
                <DateGroupItems
                  key={toCalendarDateToken(group.date)}
                  date={group.date}
                >
                  {group.assistances.map((assistance) => (
                    <EmployeeAssistanceCard
                      key={assistance.assistanceId}
                      assistance={assistance}
                      agentIdentity={
                        agentIdentities.get(assistance.employeeId) ?? {
                          name: t(
                            'automations.aiAgents.assistance.unknownAgent'
                          ),
                          identifier: assistance.employeeId,
                        }
                      }
                      onOpenExecution={
                        onOpenExecution
                          ? () => onOpenExecution(assistance)
                          : undefined
                      }
                      onOpenConversation={
                        assistance.conversationId && assistance.funnelId
                          ? () => onOpenConversation(assistance)
                          : undefined
                      }
                      onDismiss={
                        onDismissAssistance
                          ? () => onDismissAssistance(assistance)
                          : undefined
                      }
                      isDismissing={
                        dismissingAssistanceId === assistance.assistanceId
                      }
                    />
                  ))}
                </DateGroupItems>
              ))
            )}

            {hasMore || isLoadingMore ? (
              <Button
                type="button"
                variant="secondary"
                appearance="outline"
                loading={isLoadingMore}
                disabled={isLoadingMore}
                onClick={onLoadMore}
                className="w-full gap-2"
              >
                <PiArrowDown className="h-4 w-4" />
                {t('automations.aiAgents.actions.loadMore')}
              </Button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};
